/**
 * A date / datetime box that can be TYPED into.
 *
 * The native `<input type="date">` this replaces is a segmented control, and
 * a segmented control commits nothing until every segment is filled: type
 * 15/09 and tab away, or type a date into a DATETIME field without its time,
 * and the element's value is still `''` — no event fires, nothing is stored,
 * and the box goes on showing the digits that were typed. The calendar works
 * because the picker fills every segment at once. See `dateInput.ts` for the
 * verification; the asymmetry is the bug this widget removes.
 *
 * So the visible control is a text box in a stated `dd/mm/yyyy` format,
 * parsed by `parseTypedDate`, and the calendar is still one click away: the
 * button opens the native picker of a 1×1 transparent date input kept beside
 * it (rendered, because `showPicker()` refuses an element that is not, and
 * called from the button's own click, because it also refuses without a user
 * gesture).
 *
 * Committing: every keystroke that completes a date commits it, so nothing
 * depends on the field being left. What does not parse is NOT committed — the
 * stored value keeps what it had — and the box marks itself so an unfinished
 * date is visible rather than silently dropped on save.
 */

import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { CalendarDays } from 'lucide-react'

import {
  entryPlaceholder, formatForDisplay, formatForInput, hasZone, parseTypedDate,
  withLocalZone,
} from '../../dateInput'

interface Props {
  /** The stored value, in whatever shape the engine handed back. */
  value: unknown
  /** `'date'` | `'datetime-local'`. */
  type: string
  /**
   * Called with the canonical `YYYY-MM-DD[THH:MM]` (plus `:00±HH:MM` when
   * zoned), or `''` when cleared.
   */
  onChange: (v: string) => void
  disabled?: boolean
  /** Styles the text box, so callers keep their own field / cell sizing. */
  style?: CSSProperties
  title?: string
  /** Smaller calendar button for a grid cell. */
  compact?: boolean
  /**
   * The column stores an instant (TIMESTAMPTZ). It is SHOWN in local time,
   * so an edit is sent back with the browser's offset (`dateInput.hasZone`).
   * A value that already names its zone is treated the same way.
   */
  zoned?: boolean
  'data-livepreview-field'?: string
}

export default function DateField({
  value, type, onChange: emit, disabled, style, title, compact, zoned, ...rest
}: Props) {
  const zone = type === 'datetime-local' && (!!zoned || hasZone(value))
  const onChange = (v: string) => emit(zone && v ? withLocalZone(v) : v)
  const stored = formatForDisplay(value, type)
  const [text, setText] = useState(stored)
  const [editing, setEditing] = useState(false)
  const pickerRef = useRef<HTMLInputElement | null>(null)
  const textRef = useRef<HTMLInputElement | null>(null)

  // Follow the value while the box is not being typed into — a record loaded,
  // an item_change return or a cleared form must show through; a keystroke
  // must not be overwritten by the round-trip it just caused.
  useEffect(() => {
    if (!editing) setText(stored)
  }, [stored, editing])

  // Typed but not yet a date: worth showing, never worth storing.
  const parsed = parseTypedDate(text, type)
  const incomplete = text.trim() !== '' && parsed === null

  const commit = (next: string) => {
    setText(next)
    const p = parseTypedDate(next, type)
    if (p !== null) onChange(p)
  }

  const openPicker = () => {
    const el = pickerRef.current
    if (!el || disabled) return
    try {
      const withPicker = el as HTMLInputElement & { showPicker?: () => void }
      if (typeof withPicker.showPicker === 'function') {
        withPicker.showPicker()
        return
      }
    } catch {
      // Refused (not rendered / no gesture) — the box is still typeable.
    }
    textRef.current?.focus()
  }

  const btn = compact ? 22 : 28
  // The caller sized a single control; the wrapper takes that sizing and the
  // text box fills what the calendar button leaves, so a grid cell and a form
  // field keep the widths they had.
  const { flex, width, minWidth, ...boxStyle } = style || {}
  return (
    <span style={{
      position: 'relative', display: 'inline-flex', alignItems: 'center',
      gap: 4, boxSizing: 'border-box',
      flex, width, minWidth: minWidth ?? 0,
    }}>
      <input
        ref={textRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        placeholder={entryPlaceholder(type)}
        onChange={e => commit(e.target.value)}
        onFocus={() => setEditing(true)}
        // Leaving the box shows what is actually STORED: a completed entry
        // normalised ("15-9-26" → "15/09/2026"), and a refused one replaced by
        // the value it failed to change, so the form never reads back a date
        // that is not in the record. While the box is being typed into, the
        // amber border says the entry is not a date yet.
        onBlur={() => setEditing(false)}
        disabled={disabled}
        title={title || (incomplete ? `Enter a date as ${entryPlaceholder(type)}` : undefined)}
        style={{
          ...boxStyle,
          flex: 1,
          width: 'auto',
          minWidth: 0,
          ...(incomplete ? { borderColor: '#f59e0b' } : null),
        }}
        {...rest}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        title="Open calendar"
        aria-label="Open calendar"
        style={{
          flex: `0 0 ${btn}px`, width: btn, height: btn,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, borderRadius: 4,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text-secondary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <CalendarDays size={compact ? 12 : 14} />
      </button>
      {/* Rendered (1×1, transparent) so `showPicker()` will act on it. */}
      <input
        ref={pickerRef}
        type={type}
        value={formatForInput(value, type)}
        onChange={e => {
          const v = e.target.value
          setText(formatForDisplay(v, type))
          onChange(v)
        }}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden
        style={{
          position: 'absolute', right: 0, bottom: 0,
          width: 1, height: 1, padding: 0, border: 0,
          opacity: 0, pointerEvents: 'none',
        }}
      />
    </span>
  )
}
